import { useMemo } from "react";

import { useAtom, atom } from "jotai";

import { MMMState } from "@north-beam/nb-client-side/nb-api-graphql-generated";

import { useMediaPlanData } from "../hooks/use-media-plan";

const selectedMediaPlanIdAtom = atom<string | null>(null);
const isCreateMediaPlanModalOpenAtom = atom<boolean>(false);

export const useSelectedMediaPlanModal = () => {
  const [selectedMediaPlanId, setSelectedMediaPlanId] = useAtom(
    selectedMediaPlanIdAtom,
  );
  const [isCreateMediaPlanModalOpen, setIsCreateMediaPlanModalOpen] = useAtom(
    isCreateMediaPlanModalOpenAtom,
  );
  const openCreateMediaPlanModal = () => setIsCreateMediaPlanModalOpen(true);
  const closeCreateMediaPlanModal = () => {
    setIsCreateMediaPlanModalOpen(false);
    setSelectedMediaPlanId(null);
  };

  const { mediaPlanList } = useMediaPlanData();
  const selectedMediaPlan = useMemo(() => {
    const mediaPlansCopy = [...mediaPlanList, generateNewMediaPlan()];
    return (
      mediaPlansCopy.find(
        (mediaPlan) => mediaPlan.id === selectedMediaPlanId,
      ) || null
    );
  }, [selectedMediaPlanId, mediaPlanList]);

  const isDraft = useMemo(
    () => selectedMediaPlan?.state === MMMState.draft,
    [selectedMediaPlan],
  );

  const createNewMediaPlan = () => {
    setSelectedMediaPlanId("");
    openCreateMediaPlanModal();
  };

  return {
    selectedMediaPlanId,
    setSelectedMediaPlanId,
    selectedMediaPlan,
    isCreateMediaPlanModalOpen,
    openCreateMediaPlanModal,
    closeCreateMediaPlanModal,
    createNewMediaPlan,
    isDraft,
  };
};

const generateNewMediaPlan = () => ({
  __typename: "mmmMediaPlan" as const,
  id: "",
  model: null,
  model_config: null,
  date_range: {
    __typename: "MMMDateRange" as const,
    id: -1,
    start_date: null,
    end_date: null,
  },
  state: MMMState.draft,
  is_current: true,
  is_errored: false,
  breakdowns: [],
  is_manually_overridden: false,
  hidden: false,
  name: "",
  kpis: [],
  spend_model_data_columns: [],
  additional_data_inputs: [],
  calendar_of_events: [],
  media_plan_draft_id: -1,
  step_tracker: {
    __typename: "mediaPlanStepTracker" as const,
    verifiedChannels: {
      __typename: "StepVerification" as const,
      verified: false,
      verified_data: null,
      verified_by_user_id: "",
      verified_at: "",
      pending_data: [],
    },
    verifiedPerformanceGoals: {
      __typename: "StepVerification" as const,
      verified: false,
      verified_data: null,
      verified_by_user_id: "",
      verified_at: "",
      pending_data: [],
    },
    verifiedCalendarOfEvents: {
      __typename: "StepVerification" as const,
      verified: false,
      verified_data: null,
      verified_by_user_id: "",
      verified_at: "",
      pending_data: [],
    },
    verifiedAdditionalData: {
      __typename: "StepVerification" as const,
      verified: false,
      verified_data: null,
      verified_by_user_id: "",
      verified_at: "",
      pending_data: [],
    },
  },
});
